import { useEffect } from "react";
import { useAppSelector } from "app/store";
import { useTeamManagement } from "./useTeamManagement";
import { useNotifications } from "features/notifications/hooks";
import { Team } from "models/schemas";
import { useTeamName } from "pages/team_settings/hooks";

/**
 * Hook to handle team switching when an object from a different team is loaded
 * @param teamName The data source team name
 * @param assigneeTeam Optional task assignee team — when the user is a member of this team,
 *   skip the auto-switch so they remain on their current header team (cross-team task access).
 */
export const useCheckTeamSwitch = (
  teamName: string,
  assigneeTeam?: string | null,
) => {
  const { currentResourceTeam, headerSelectedTeam } = useTeamName();
  const allTeams: any = useAppSelector((state) => state.teamSettings?.allTeams);

  const {
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
  } = useTeamManagement();
  const { addNotification } = useNotifications();

  useEffect(() => {
    if (!teamName || !allTeams?.teams) {
      return;
    }

    // Don't redirect the user off the assignee team if they navigated from it (or aren't on the owner team at all).
    const isAssigneeTeamMember =
      !!assigneeTeam &&
      allTeams.teams.some((team: Team) => team.teamId === assigneeTeam);
    const isOwnerTeamMember = allTeams.teams.some(
      (team: Team) => team.teamId === teamName,
    );
    const viewingFromAssigneeTeam =
      !!assigneeTeam && headerSelectedTeam === assigneeTeam;
    if (
      isAssigneeTeamMember &&
      (viewingFromAssigneeTeam || !isOwnerTeamMember)
    ) {
      return;
    }

    // If teamName is a sub-team, resolve to its parent for header display only
    // When users navigate to a sub-team audit, teamName is the sub-team
    // (e.g., itx_compliance_apac). This resolves it to the parent for the header
    const currentTeam = allTeams.teams.find(
      (team: Team) => team.teamId === teamName,
    );
    const parentTeamName = currentTeam?.parentTeamResourceId || teamName;
    const parentTeam = currentTeam?.parentTeamResourceId
      ? allTeams.teams.find((team: Team) => team.teamId === parentTeamName)
      : currentTeam;

    const isTeamMember = allTeams.teams.some(
      (team: Team) => team.teamId === teamName,
    );

    if (isTeamMember) {
      // Header always shows the parent team (dropdown selection)
      const headerTeamName = parentTeam ? parentTeamName : teamName;
      const teamDescription = parentTeam?.teamDescription || "";

      if (headerTeamName !== headerSelectedTeam) {
        updateHeaderSelectedTeam(headerTeamName, teamDescription);
      }

      if (teamName !== currentResourceTeam) {
        updateSelectedTeam(teamName);

        // Only update team-specific data when data team changes
        const userCriticalMap: { [key: string]: boolean } = {};
        const teamSettingsMap = new Map<string, Team>();

        allTeams.teams.forEach((team: Team) => {
          userCriticalMap[team.teamId] = team.isCritical;
          teamSettingsMap.set(team.teamId, team);
        });

        updateUserCriticalnessInTeam(teamName, userCriticalMap);
        updateTeamDataAndSchemas(teamName, teamSettingsMap);
      }
    } else {
      // Guest logic
      if (headerSelectedTeam !== "guest") {
        updateHeaderSelectedTeam("guest", "Guest");
        addNotification({
          type: "info",
          header: "Switched to Guest view",
          content:
            "You are viewing a guest task. To return to your tasks, select your team from the header dropdown.",
          dismissible: true,
          timeout: 10000,
        });
      }
      if (currentResourceTeam !== teamName) {
        updateSelectedTeam(teamName);
      }
    }
  }, [
    teamName,
    assigneeTeam,
    headerSelectedTeam,
    currentResourceTeam,
    allTeams,
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
  ]);
};
